import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type AdminImport } from '../../api/admin';
import { confirm } from '../../components/ConfirmDialog';

export default function AdminImportsPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedClient, setSelectedClient] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [viewImport, setViewImport] = useState<string | null>(null);
  const [viewTab, setViewTab] = useState<'invoices' | 'parties' | 'products' | 'expenses'>('invoices');

  const { data: imports = [], isLoading } = useQuery({ queryKey: ['admin-imports'], queryFn: () => adminApi.listImports(), staleTime: 30 * 1000 });
  const { data: clients = [] } = useQuery({ queryKey: ['admin-clients', ''], queryFn: () => adminApi.listClients(), staleTime: 30 * 1000 });
  const { data: importDetail } = useQuery({
    queryKey: ['admin-import-data', viewImport],
    queryFn: () => adminApi.getImportData(viewImport!),
    enabled: !!viewImport,
    staleTime: 30 * 1000,
  });
  const del = useMutation({ mutationFn: adminApi.deleteImport, onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-imports'] }) });

  async function handleUpload(file: File) {
    if (!selectedClient) { setUploadError('Please select a client first'); return; }
    setUploading(true); setUploadError(null);
    try {
      await adminApi.uploadImport(selectedClient, file);
      qc.invalidateQueries({ queryKey: ['admin-imports'] });
    } catch (err) { setUploadError(err instanceof Error ? err.message : 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  const viewData = importDetail?.data as Record<string, unknown[]> | undefined;

  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">Data Imports</h1></div>

      {/* Upload section */}
      <div className="card" style={{ marginBottom: '1.25rem', padding: '1rem 1.25rem' }}>
        <div style={{ fontWeight: 700, marginBottom: '0.75rem' }}>Upload Desktop Export ZIP</div>
        {uploadError && <div className="error-box" style={{ marginBottom: '0.75rem' }}>{uploadError}</div>}
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)} style={{ minWidth: 240 }}>
            <option value="">— Select Client —</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.business} ({c.name})</option>)}
          </select>
          <input ref={fileRef} type="file" accept=".zip" style={{ flex: 1 }}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); }}
            disabled={uploading || !selectedClient} />
          {uploading && <span className="spinner" style={{ width: 18, height: 18 }} />}
        </div>
      </div>

      {/* Imports list */}
      <div className="card">
        {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
          : imports.length === 0
          ? <div className="empty-state"><div className="empty-state-icon">📦</div><div className="empty-state-text">No imports yet</div></div>
          : (
            <table>
              <thead>
                <tr><th>Client</th><th>Business</th><th>Import Date</th><th>Invoices</th><th>Parties</th><th>Products</th><th>Expenses</th><th style={{ width: 120 }}>Actions</th></tr>
              </thead>
              <tbody>
                {imports.map((imp: AdminImport) => (
                  <tr key={imp.id}>
                    <td style={{ fontWeight: 600 }}>{imp.client?.business ?? '—'}</td>
                    <td style={{ color: 'var(--text-2)' }}>{imp.businessName ?? '—'}</td>
                    <td style={{ fontSize: '0.83rem', color: 'var(--text-muted)' }}>{imp.importDate.slice(0, 10)}</td>
                    <td style={{ textAlign: 'center' }}>{imp.invoiceCount}</td>
                    <td style={{ textAlign: 'center' }}>{imp.partyCount}</td>
                    <td style={{ textAlign: 'center' }}>{imp.productCount}</td>
                    <td style={{ textAlign: 'center' }}>{imp.expenseCount}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setViewImport(imp.id); setViewTab('invoices'); }}>View</button>
                        <button className="btn btn-danger btn-sm" onClick={async () => { if (await confirm({ message: 'Delete this import?', danger: true })) del.mutate(imp.id); }}>Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {/* Data viewer modal */}
      {viewImport && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewImport(null)}>
          <div className="modal" style={{ maxWidth: 900, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <span className="modal-title">Import Data</span>
              <button className="modal-close" onClick={() => setViewImport(null)}>×</button>
            </div>
            <div style={{ borderBottom: '1px solid var(--border)', display: 'flex', gap: 0 }}>
              {(['invoices', 'parties', 'products', 'expenses'] as const).map(tab => (
                <button key={tab} type="button" onClick={() => setViewTab(tab)}
                  style={{ padding: '0.6rem 1.1rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: viewTab === tab ? 700 : 400, borderBottom: `2px solid ${viewTab === tab ? 'var(--primary)' : 'transparent'}`, color: viewTab === tab ? 'var(--primary)' : 'var(--text-2)', textTransform: 'capitalize' }}>
                  {tab} ({viewData?.[tab]?.length ?? '…'})
                </button>
              ))}
            </div>
            <div style={{ overflow: 'auto', flex: 1, padding: '0.5rem' }}>
              {!viewData ? <div style={{ padding: '2rem', textAlign: 'center' }}><span className="spinner" /></div> : (
                <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {JSON.stringify(viewData[viewTab]?.slice(0, 10) ?? [], null, 2)}
                </pre>
              )}
              {viewData && (viewData[viewTab]?.length ?? 0) > 10 && (
                <p style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.5rem' }}>
                  Showing first 10 of {viewData[viewTab].length} records
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
